/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { useFormContext, Controller } from 'react-hook-form';
import { FormattedMessage, FormattedHTMLMessage, defineMessages, injectIntl } from 'react-intl';
import { isValidNumber } from 'libphonenumber-js';
import { Input as BaseInput, Form, Message, Icon, Label as BaseLabel } from 'semantic-ui-react';

import { FormLabel as BaseFormLabel } from '../../../../components';
import EmailField from './EmailField';
import PhoneField from './PhoneField';
import styles from '../../../../styles';

const Label = styled(BaseFormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

const Input = styled(BaseInput)`
    & input {
        background-color: ${props =>
            props.readOnly ? '#fffaf3' : '#fff'} !important;
    }
`;

const intlMessages = defineMessages({
    firstNameError: {
        id: 'pages.suppliers.supplierForm.firstNameError',
        defaultMessage: 'Enter client first name',
    },
    emailError: {
        id: 'pages.suppliers.supplierForm.emailError',
        defaultMessage: 'Invalid email',
    },
    phoneError: {
        id: 'pages.suppliers.supplierForm.phoneError',
        defaultMessage: 'Invalid phone number',
    },
    noCountryCodeError: {
        id: 'pages.suppliers.supplierForm.noCountryCodeError',
        defaultMessage: 'Phone number should start with country code',
    },
    passwordError: {
        id: 'pages.suppliers.supplierForm.passwordError',
        defaultMessage: 'Enter password',
    },
    repeatPasswordError: {
        id: 'pages.suppliers.supplierForm.repeatPasswordError',
        defaultMessage: 'Repeat password',
    },
    passwordDoesntMatchError: {
        id: 'pages.suppliers.supplierForm.passwordDoesntMatchError',
        defaultMessage: "Password doesn't match",
    },
});

type SupplierFormProps = {
    onUserLookup: Function,
    resetUserLookup: Function,
    userLookupStatus: string,
    userLookupUser: Object,
    readOnly: boolean,
    mode?: string,
    intl: Object,
};

function SupplierForm({
    userLookupStatus,
    userLookupUser,
    onUserLookup,
    resetUserLookup,
    readOnly,
    mode,
    intl,
}: SupplierFormProps) {
    const { control, watch, formState: { errors } } = useFormContext();

    const validatePhone = value => {
        if (!value) return true;
        if (value[0] !== '+') return intl.formatMessage(intlMessages.noCountryCodeError);
        if (!isValidNumber(value)) return intl.formatMessage(intlMessages.phoneError);
        return true;
    };

    const isEditMode = mode === 'edit';

    return (
        <Form error={!!errors.root} autoComplete="off">
            {userLookupUser && (
                <Message info>
                    <Icon name="info" />
                    <FormattedHTMLMessage
                        id="pages.suppliers.supplierForm.supplierExistsMessage"
                        defaultMessage="<strong>{firstName} {lastName}<strong> already exists in our system, you can add it to your salon"
                        values={{
                            firstName: userLookupUser.firstName,
                            lastName: userLookupUser.lastName,
                        }}
                    />
                </Message>
            )}
            {errors.root && (
                <Message error>
                    <Icon name="warning sign" />
                    {errors.root.message}
                </Message>
            )}
            <Form.Group widths="equal">
                <Form.Field error={!!errors.firstName}>
                    <FormattedMessage
                        id="pages.suppliers.supplierForm.firstName"
                        defaultMessage="First Name"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.firstNameError) }}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <Input {...field} readOnly={readOnly} fluid />
                        )}
                    />
                    {errors.firstName && (
                        <BaseLabel color="red" pointing basic>
                            {errors.firstName.message}
                        </BaseLabel>
                    )}
                </Form.Field>
                <Form.Field>
                    <FormattedMessage
                        id="pages.suppliers.supplierForm.lastName"
                        defaultMessage="Last Name"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <Input {...field} readOnly={readOnly} fluid />
                        )}
                    />
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.email}>
                    <FormattedMessage
                        id="pages.suppliers.supplierForm.email"
                        defaultMessage="E-mail"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="email"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.emailError) }}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <EmailField
                                {...field}
                                onBlur={() => {
                                    field.onBlur();
                                    if (field.value) {
                                        onUserLookup?.({ email: field.value, role: 'SUPPLIER' });
                                    } else {
                                        resetUserLookup?.();
                                    }
                                }}
                                userLookupStatus={userLookupStatus}
                                disabled={readOnly}
                                autoComplete="new-password"
                            />
                        )}
                    />
                    {errors.email && (
                        <BaseLabel color="red" pointing basic>
                            {errors.email.message}
                        </BaseLabel>
                    )}
                </Form.Field>
                <Form.Field error={!!errors.phone}>
                    <FormattedMessage
                        id="pages.suppliers.supplierForm.phone"
                        defaultMessage="Phone"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="phone"
                        control={control}
                        rules={{ validate: validatePhone }}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <PhoneField
                                {...field}
                                onChange={(e, data) => field.onChange(data?.value ?? e)}
                                onBlur={() => {
                                    field.onBlur();
                                    if (field.value) {
                                        onUserLookup?.({
                                            phone: field.value.replace(/[^+\d]+/g, ''),
                                            role: 'SUPPLIER',
                                        });
                                    } else {
                                        resetUserLookup?.();
                                    }
                                }}
                                userLookupStatus={userLookupStatus}
                                disabled={readOnly}
                                autoComplete="new-password"
                            />
                        )}
                    />
                    {errors.phone && (
                        <BaseLabel color="red" pointing basic>
                            {errors.phone.message}
                        </BaseLabel>
                    )}
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.password}>
                    <FormattedMessage
                        id="pages.suppliers.supplierForm.password"
                        defaultMessage="Password"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="password"
                        control={control}
                        rules={{
                            required: (!isEditMode && !readOnly)
                                ? intl.formatMessage(intlMessages.passwordError)
                                : false,
                        }}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <Input
                                {...field}
                                type="password"
                                autoComplete="new-password"
                                disabled={readOnly}
                                fluid
                            />
                        )}
                    />
                    {errors.password && (
                        <BaseLabel color="red" pointing basic>
                            {errors.password.message}
                        </BaseLabel>
                    )}
                </Form.Field>
                <Form.Field error={!!errors.repeatPassword}>
                    <FormattedMessage
                        id="pages.suppliers.supplierForm.repeatPassword"
                        defaultMessage="Repeat Password"
                        children={txt => <Label>{txt}</Label>}
                    />
                    <Controller
                        name="repeatPassword"
                        control={control}
                        rules={{
                            required: (!isEditMode && !readOnly)
                                ? intl.formatMessage(intlMessages.repeatPasswordError)
                                : false,
                            validate: value => {
                                const password = watch('password');
                                if (!value && !password) return true;
                                if (readOnly) return true;
                                if (value !== password) return intl.formatMessage(intlMessages.passwordDoesntMatchError);
                                return true;
                            },
                        }}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <Input
                                {...field}
                                type="password"
                                autoComplete="new-password"
                                disabled={readOnly}
                                fluid
                            />
                        )}
                    />
                    {errors.repeatPassword && (
                        <BaseLabel color="red" pointing basic>
                            {errors.repeatPassword.message}
                        </BaseLabel>
                    )}
                </Form.Field>
            </Form.Group>
        </Form>
    );
}

export default injectIntl(SupplierForm);
